import { useMemo, useRef } from 'react';
import { IoClose } from 'react-icons/io5';
import ReactSelect from 'react-select';
import CreatableSelect from 'react-select/creatable';
import '../styles/Input.css';
import UserAvatar from './UserAvatar';
import { shouldRenderAutoRequiredMark } from '../utils/requiredLabel';

const Select = ({ label = '', showIcon = false, isDisabled = false, isOptionDisabled, icon, placeholder, options, loading, name, value, onChange, onInputChange, onBlur, error, touched, isMulti = false, isCreatable = false, isSearch = true, customStyles = {}, className = '', menuPortalTarget = null, menuPlacement = 'auto', required = true, showOptionIconInMenu = false, isClearable = false, tabIndex }) => {
        const selectRef = useRef(null);
    const resolvedMenuPortalTarget = useMemo(() => {
        if (menuPortalTarget === undefined || menuPortalTarget === null) {
            return typeof document !== 'undefined' ? document.body : null;
        }
        return menuPortalTarget;
    }, [menuPortalTarget]);
    const shouldShowAutoRequired = shouldRenderAutoRequiredMark(label, required);

    const defaultStyles = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            background: 'transparent',
            border: 'none',
            fontSize: '13px',
            marginTop: '0px',
            cursor: state.isDisabled ? "not-allowed" : "pointer",
            boxShadow: 'none',
            minHeight: "28px",
            '&:hover': {
                border: 'none',
            },
        }),
        placeholder: (baseStyles) => ({
            ...baseStyles,
            color: '#C4C0D0',
            fontSize: '13px',
            fontWeight: '400',
        }),
        container: (baseStyles) => ({
            ...baseStyles,
            width: "100%",
        }),
        valueContainer: (baseStyles) => ({
            ...baseStyles,
            padding: '0',
            paddingLeft: '5px',
        }),
        input: (baseStyles) => ({
            ...baseStyles,
            margin: '0',
            padding: '0',
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
        dropdownIndicator: (baseStyles) => ({
            ...baseStyles,
            padding: '0',
            color: '#000',
        }),
        singleValue: (baseStyles) => ({
            ...baseStyles,
            fontSize: '13px',
        }),
        menu: (baseStyles) => ({
            ...baseStyles,
            zIndex: 999999,
        }),
        menuPortal: (baseStyles) => ({
            ...baseStyles,
            zIndex: 999999,
        }),
        option: (baseStyles, state) => ({
            ...baseStyles,
            fontSize: '13px',
            cursor: state.isDisabled ? "not-allowed" : "pointer",
        }),
    }

    const mergedStyles = {
        ...defaultStyles,
        ...customStyles,
    };

    // Helper to merge style functions if they exist in both
    ['control', 'placeholder', 'container', 'valueContainer', 'input', 'dropdownIndicator', 'singleValue', 'menu', 'menuPortal', 'option'].forEach(key => {
        if (defaultStyles[key] && customStyles[key]) {
            mergedStyles[key] = (base, state) => {
                const defaultResult = defaultStyles[key](base, state);
                return customStyles[key](defaultResult, state);
            };
        }
    });

    const SelectComponent = isCreatable ? CreatableSelect : ReactSelect;

    const formatOptionLabel = (option, meta) => {
        if (showOptionIconInMenu && option.icon && meta?.context === 'menu') {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {typeof option.icon === 'string'
                        ? <img src={option.icon} alt={option.label} style={{ width: 16, height: 16, objectFit: 'contain', flexShrink: 0 }} />
                        : <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>{option.icon}</span>}
                    <span>{option.label}</span>
                </div>
            );
        }
        if (option.showIcon) {
            if (option.image) {
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <UserAvatar username={option.label} image={option.image} size={20} fontSize="10px" />
                        <span>{option.label}</span>
                    </div>
                );
            }
            else if (!option.image) {
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <UserAvatar username={option.label} image={option.image} size={20} fontSize="10px" />
                        <span>{option.label}</span>
                    </div>
                );
            }
        }
        if (option.badge) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 8 }}>
                    <span>{option.label}</span>
                    <span
                        style={{
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#4f19e2',
                            background: '#f5f3ff',
                            border: '1px solid #ede9fe',
                            borderRadius: '10px',
                            padding: '2px 8px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                        }}
                    >
                        {option.badge}
                    </span>
                </div>
            );
        }
        if (option.brand && option.priceSource) {
            return (
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span>{option.description}</span>
                        <span style={{ color: '#666', fontSize: '0.7em' }}>{option.product} {option.manufacturer ? (`(${option.manufacturer})`) : ''}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ color: '#333', fontSize: '0.9em', fontWeight: '500' }}>PKR {option.brand}</div>
                        <div style={{ color: '#666', fontSize: '0.75em' }}>{option.priceSource === "own" ? "JHPL" : option.priceSource}</div>
                    </div>
                </div>
            );
        }
        if (option.brand) {
            return (
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span>{option.description}</span>
                        <span style={{ color: '#666', fontSize: '0.7em' }}>{option.product} {option.manufacturer ? (`(${option.manufacturer})`) : ''}</span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ color: '#666', fontSize: '0.9em' }}>{option.brand}</span>
                        <span style={{ color: '#666', fontSize: '0.9em' }}>{option.priceSource}</span>
                    </div>
                </div>
            );
        }
        return option.label;
    };

    return (
        <div className={`input_wrapper ${className}`} data-field-name={name}>
            <div className={`input_container ${isDisabled ? 'disabled' : ''}`}>
                {(label || shouldShowAutoRequired) ? (
                    <label style={{ display: "flex", gap: "5px" }}>
                        {label}
                        {shouldShowAutoRequired && <span style={{ color: "red", fontSize: "13px" }}>*</span>}
                    </label>
                ) : null}
                {
                    showIcon ?
                        <div className='d-flex align-items-center gap-2'>
                            <span className='select_icon'>
                                {icon}
                            </span>
                            <SelectComponent
                                isLoading={loading}
                                innerRef={selectRef}
                                options={options}
                                placeholder={placeholder}
                                styles={mergedStyles}
                                name={name}
                                value={value}
                                onChange={onChange}
                                onInputChange={onInputChange}
                                formatOptionLabel={formatOptionLabel}
                                onBlur={onBlur}
                                isMulti={isMulti}
                                isDisabled={isDisabled}
                                isOptionDisabled={isOptionDisabled}
                                isClearable={isClearable || isCreatable}
                                isSearchable={isSearch}
                                tabIndex={tabIndex}
                                menuPlacement={menuPlacement}
                                menuShouldScrollIntoView
                                menuPortalTarget={resolvedMenuPortalTarget}
                                menuPosition={resolvedMenuPortalTarget ? 'fixed' : 'absolute'}
                                {...(isCreatable && {
                                    formatCreateLabel: (userInput) => `Create "${userInput}"`,
                                    noOptionsMessage: () => "Type to create new tag"
                                })}
                                components={{
                                    ClearIndicator: (clearProps) => {
                                        return (
                                            <div className="select-clear-button" {...clearProps.innerProps}>
                                                <IoClose />
                                                {clearProps.children} {/* This will render the default clear icon */}
                                            </div>
                                        )
                                    }
                                }}
                            />
                        </div> :
                        <SelectComponent
                            isLoading={loading}
                            options={options}
                            placeholder={placeholder}
                            formatOptionLabel={formatOptionLabel}
                            styles={mergedStyles}
                            name={name}
                            value={value}
                            onChange={onChange}
                            onInputChange={onInputChange}
                            onBlur={onBlur}
                            isMulti={isMulti}
                            isDisabled={isDisabled}
                            isOptionDisabled={isOptionDisabled}
                            isClearable={isClearable || isCreatable}
                            isSearchable={isSearch}
                            tabIndex={tabIndex}
                            menuPlacement={menuPlacement}
                            menuShouldScrollIntoView
                            menuPortalTarget={resolvedMenuPortalTarget}
                            menuPosition={resolvedMenuPortalTarget ? 'fixed' : 'absolute'}
                            {...(isCreatable && {
                                formatCreateLabel: (userInput) => `Create "${userInput}"`,
                                noOptionsMessage: () => "Type to create new tag"
                            })}
                            components={{
                                ClearIndicator: (clearProps) => {
                                    return (
                                        <div className="select-clear-button" {...clearProps.innerProps}>
                                            <IoClose />
                                            {clearProps.children} {/* This will render the default clear icon */}
                                        </div>
                                    )
                                }
                            }}
                        />
                }
            </div>
            {(() => {
                const hasSelectedValue = (() => {
                    if (value === null || value === undefined || value === '') return false;
                    if (Array.isArray(value)) return value.length > 0;
                    if (typeof value === 'object') {
                        return (value.value !== undefined && value.value !== '' && value.value !== null) ||
                               (value.label !== undefined && value.label !== '' && value.label !== null) ||
                               Object.keys(value).length > 0;
                    }
                    return String(value).trim().length > 0;
                })();

                const isRequiredError = typeof error === 'string' && error.toLowerCase().includes('required');
                const shouldShowError = touched && error && (!hasSelectedValue || !isRequiredError);

                return shouldShowError ? (
                    <div className="error-message" style={{ color: '#ff0000de', fontSize: '12px', marginTop: "3px", marginLeft: "5px" }}>
                        {error}
                    </div>
                ) : null;
            })()}
        </div>
    )
}

export default Select
